// --- React & Redux
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
// --- Actions & Hooks
import { useUploadDialog } from 'datastage/components/Dialogs/dialogs/upload';
import { usePermissions } from 'datastage/utils/permissions';
import { useToasts } from 'datastage/utils/hooks/useToasts';
import { useLoading } from 'datastage/utils/hooks/useLoading';
import { useDateMathData } from './hooks/useDateMathData';
// --- External UI Libraries
import Button from '@dtci/spellbook/Button';
import Typography from '@dtci/spellbook/Typography';
import { Col, Row } from 'antd';
// --- Components
import { DateMathGrid } from './components/grid';
import { DateMathSearchForm } from './components/search-form';
import { MacrosDrawer } from './components/macros-drawer';
import ToastNotificationProvider from 'datastage/components/ToastNotifications';
import { PaginationBar } from 'datastage/components/PaginationBar';
import { TwoRowsLayout } from './layout/two-row';
// --- Utilities
import { datastageApi } from 'datastage/api';
import { exportToCsv } from 'datastage/utils/export/exportToCsv';
import { getItemRange, getAllItemsRange } from 'datastage/utils/pagination';
import { parseSearchParams, updateSearchParams } from 'datastage/utils/urlParams';
import { useStoredItemsPerPageSync } from 'datastage/utils/hooks/useStoredItemsPerPageSync';
import { PAGINATION_STORAGE_KEYS } from 'datastage/utils/paginationPreferences';

/**
 * Date Math Page
 *
 * Features:
 * - Displays a paginated list of Date Math jobs
 * - Provides search, upload, export, and refresh functionality
 * - Handles loading and toast notifications
 * - Uses a two-row layout for search and grid
 * - Implements refreshDateMath flag pattern for clean separation of concerns
 * - Persists page size preference to localStorage automatically
 *
 * @component
 * @returns {JSX.Element} The rendered date math jobs page.
 */
export const DateMathPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { showErrorToast } = useToasts();
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { fetchDateMathJobs, updateDateMath, dateMathRange, itemsPerPage } = useDateMathData();
    const { openUploadDialog } = useUploadDialog();
    const { isBulkDateMathToolUser } = usePermissions();
    const autoRefreshRef = useRef(null);

    const { totalPages, currentPage, totalItems, refreshDateMath, isMacrosOpen, pageSizeOptions } = useSelector(
        (state) => state.dateMath,
    );
    const { showCalculations } = useSelector((state) => state.dateMathCalculations);

    /**
     * Sync persisted page size preference from localStorage on mount.
     * If stored preference differs from Redux state, automatically updates Redux.
     */
    useStoredItemsPerPageSync({
        storageKey: PAGINATION_STORAGE_KEYS.dateMath,
        itemsPerPage,
        rangeKey: 'dateMathRange',
        onSync: updateDateMath,
    });

    /**
     * Listen for refreshDateMath flag and refresh the grid.
     * This pattern allows other components (like drawers) to trigger refreshes
     * without duplicating fetch logic.
     */
    useEffect(() => {
        if (refreshDateMath) {
            handleRefresh();
            updateDateMath({ refreshDateMath: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshDateMath]);

    /**
     * Auto-refresh jobs list every 5 seconds for real-time status updates.
     * Pauses when calculations modal or macros drawer is open.
     */
    useEffect(() => {
        // Clear any existing interval
        if (autoRefreshRef.current) {
            clearInterval(autoRefreshRef.current);
            autoRefreshRef.current = null;
        }

        // Don't auto-refresh if modal/drawer is open
        if (showCalculations || isMacrosOpen) {
            return;
        }

        // Set up auto-refresh interval
        autoRefreshRef.current = setInterval(() => {
            handleRefresh();
        }, 5000);

        // Cleanup on unmount or when dependencies change
        return () => {
            if (autoRefreshRef.current) {
                clearInterval(autoRefreshRef.current);
                autoRefreshRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showCalculations, isMacrosOpen, dateMathRange]);

    /**
     * Handle page change for pagination (←/→ buttons).
     * Fetches directly without updating URL (TAP-UI pattern).
     * Page size changes go through URL → search form handles those.
     */
    const handlePageChange = async (pageNumber) => {
        if (pageNumber === currentPage) return;

        const nextPageRange = getItemRange(pageNumber, itemsPerPage);
        const data = await fetchDateMathJobs(nextPageRange);

        if (data) {
            updateDateMath({
                jobs: data?.content ?? [],
                totalPages: data?.totalPages ?? 0,
                totalItems: data?.totalElements ?? 0,
                currentPage: data?.pageable?.pageNumber ?? 0,
                dateMathRange: nextPageRange,
            });
        }
    };

    /**
     * Handle page size change from dropdown.
     * Updates URL with new page size - the search form will handle the fetch.
     * This prevents duplicate fetches (TAP-UI pattern).
     */
    const handlePageSizeChange = (newSize) => {
        if (newSize === itemsPerPage) return;

        // Update URL with new page size
        // Search form will see URL change and fetch with new size
        const currentParams = parseSearchParams(location.search);
        updateSearchParams(location, navigate, {
            ...currentParams,
            pageSize: newSize,
        });
    };

    /**
     * Refresh the date math jobs list from the API.
     */
    const handleRefresh = async () => {
        const data = await fetchDateMathJobs(dateMathRange);

        if (data) {
            updateDateMath({
                jobs: data?.content ?? [],
                totalItems: data?.totalElements ?? 0,
                totalPages: data?.totalPages ?? 0,
                currentPage: data?.pageable?.pageNumber ?? 0,
            });
        }
    };

    /**
     * Export all date math jobs to CSV.
     * Fetches all records using the total count from the current result set.
     */
    const handleExport = async () => {
        const exportRange = getAllItemsRange(totalItems);
        const data = await fetchDateMathJobs(exportRange);

        if (data?.code) {
            showErrorToast(data?.message || 'Failed to export date math jobs.');
            return;
        }

        const exportFields = [
            'id',
            'spaJobType',
            'spaJobStatus',
            'startedByUser',
            'targetCount',
            'progressCount',
            'errorCount',
            'createdAt',
            'modifiedAt',
            'completedAt',
        ];
        const filteredContent = (data?.content || []).map((item) =>
            exportFields.reduce((obj, key) => {
                obj[key] = item[key];
                return obj;
            }, {}),
        );

        exportToCsv(filteredContent, 'datemath_jobs');
    };

    /**
     * Open the upload dialog for uploading Date Math job files.
     */
    const onOpenUploadDialog = () => {
        openUploadDialog(async ({ file }) => {
            if (!file) return;

            const formData = new FormData();
            formData.append('file', Array.isArray(file) ? file[0] : file);

            setLoadingTrue();
            try {
                const res = await datastageApi.uploadDateMathJob(formData);
                if (res?.code) {
                    showErrorToast(res?.message || 'Failed to upload date math XLSX.');
                }
            } catch (error) {
                showErrorToast(error?.message || 'Failed to upload date math XLSX.');
                console.error('Error uploading date math XLSX:', error);
            } finally {
                setLoadingFalse();
                // Trigger refresh after upload
                updateDateMath({ refreshDateMath: true });
            }
        });
    };

    /**
     * Open the macros drawer.
     */
    const handleOpenMacrosDrawer = () => updateDateMath({ isMacrosOpen: true });

    return (
        <>
            <Row
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 20,
                }}
            >
                <Col>
                    <Typography variant="T50" style={{ marginLeft: 20 }}>
                        Date Math Jobs
                    </Typography>
                </Col>
                <Col style={{ display: 'flex', gap: 8 }}>
                    <Button
                        iconType="fa-solid fa-gears"
                        labelText="metrics"
                        styleType="link"
                        onClick={handleOpenMacrosDrawer}
                    >
                        Macros
                    </Button>
                    <Button
                        iconType="fa-solid fa-arrow-up-from-bracket"
                        labelText="Upload"
                        styleType="link"
                        onClick={onOpenUploadDialog}
                        disabled={!isBulkDateMathToolUser}
                    >
                        Upload Date Math Job
                    </Button>
                    <Button
                        iconType="fa-solid fa-arrow-down-to-line"
                        labelText="Export"
                        styleType="link"
                        onClick={handleExport}
                        disabled={totalItems === 0}
                    >
                        Export
                    </Button>
                    <Button
                        iconType="fa-solid fa-rotate"
                        labelText="Refresh"
                        styleType="link"
                        onClick={handleRefresh}
                    >
                        Refresh
                    </Button>
                </Col>
            </Row>

            <TwoRowsLayout>
                <main data-position="top">
                    <DateMathSearchForm />
                </main>
                <main data-position="bottom">
                    <DateMathGrid />
                </main>
            </TwoRowsLayout>

            <PaginationBar
                totalPages={totalPages}
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={pageSizeOptions}
            />

            {/* Macros drawer for creating date math macros */}
            <MacrosDrawer />

            <ToastNotificationProvider />
        </>
    );
};
